import React, {createContext, Dispatch, FC, SetStateAction, useContext, useEffect, useRef, useState,} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserByToken} from './_requests'

/**
 * Authentication core for Davinci.
 *
 * Holds the signed-in session in React context and bridges it to
 * `localStorage` (via `AuthHelpers`). The flow:
 *
 * 1. {@link AuthProvider} exposes `auth`, `currentUser`, and the
 *    `saveAuth` / `setCurrentUser` / `logout` mutators to the whole tree.
 * 2. {@link AuthInit} runs once on boot: if a persisted credential exists it
 *    resolves the user via `getUserByToken`, otherwise it logs out. A splash
 *    screen is shown until that request settles.
 * 3. {@link useAuth} is the hook every component uses to read/Mutate the
 *    session.
 *
 * See `docs/state-management.md` and `docs/routing-and-permissions.md`.
 *
 * @module app/modules/auth/core/Auth
 */

/**
 * Shape of the authentication context value shared across the app.
 */
type AuthContextProps = {
    auth: AuthModel | undefined
    saveAuth: (auth: AuthModel | undefined) => void
    currentUser: UserModel | undefined
    setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
    logout: () => void
}

const initAuthContextPropsState = {
    auth: authHelper.getAuth(),
    saveAuth: () => { /** */
    },
    currentUser: undefined,
    setCurrentUser: () => { /** */
    },
    logout: () => { /** */
    },
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

/**
 * Hook to read and mutate the current authentication session.
 *
 * @returns The active {@link AuthContextProps} value.
 */
const useAuth: () => AuthContextProps = () => {
    return useContext(AuthContext)
}

/**
 * Context provider that owns the session state.
 *
 * Seeds `auth` from `localStorage`, persists every change back through
 * `AuthHelpers`, and clears the credential keys on {@link AuthContextProps.logout}.
 *
 * @param props.children  The application subtree that needs auth access.
 */
const AuthProvider: FC = ({children}) => {
    const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
    const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
    const saveAuth = (auth: AuthModel | undefined) => {
        setAuth(auth)
        if (auth) {
            authHelper.setAuth(auth)
        } else {
            authHelper.removeAuth()
        }
    }

    const logout = () => {
        saveAuth(undefined)
        setCurrentUser(undefined)
        localStorage.removeItem('clientId');
        localStorage.removeItem('credential');
    }

    return (
        <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
            {children}
        </AuthContext.Provider>
    )
}

/**
 * Bootstraps the session before the app renders.
 *
 * On mount, if a persisted `auth` and `credential` are present it resolves the
 * current user via `getUserByToken`; any failure forces a logout. A
 * `LayoutSplashScreen` is shown until the lookup settles, guaranteeing the
 * router never renders a half-authenticated state. Uses a `didRequest` ref to
 * guard against React 18 double-invocation.
 *
 * @param props.children  The application subtree to reveal once auth resolves.
 */
const AuthInit: FC = ({children}) => {
    const {auth, logout, setCurrentUser} = useAuth()
    const didRequest = useRef(false)
    const [showSplashScreen, setShowSplashScreen] = useState(true)
    // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
    useEffect(() => {
        const credential = localStorage.getItem('credential') ?? '';

        const requestUser = async (credential: string) => {
            try {
                if (!didRequest.current) {
                    const {data} = await getUserByToken(credential);
                    if (data) {
                        setCurrentUser(data)
                    }
                }
            } catch (error) {
                console.error(error)
                if (!didRequest.current) {
                    logout()
                }
            } finally {
                setShowSplashScreen(false)
            }

            return () => (didRequest.current = true)
        }

        if (auth && credential) {
            requestUser(credential)
        } else {
            logout()
            setShowSplashScreen(false)
        }
    }, [])

    return showSplashScreen ? <LayoutSplashScreen/> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
